import React, { useState } from 'react';

interface TasksPageProps {
  onBack: () => void;
}

type Task = {
  id: string;
  title: string;
  desc: string;
  rewardValue: string;
  rewardIcon: string;
  currentValue: number;
  targetValue: number;
  category: 'daily' | 'weekly' | 'starter';
};

const INITIAL_TASKS: Task[] = [
  { id: '1', title: '15 Dakika İzle', desc: 'Herhangi bir yayını 15 dakika izle', rewardValue: '10', rewardIcon: '💎', currentValue: 15, targetValue: 15, category: 'daily' },
  { id: '2', title: 'Mesaj Gönder', desc: 'Canlı yayınlarda 3 farklı mesaj at', rewardValue: '5', rewardIcon: '🟡', currentValue: 1, targetValue: 3, category: 'daily' },
  { id: '3', title: 'Kalp At', desc: 'Yayıncılara toplam 50 beğeni gönder', rewardValue: '15', rewardIcon: '💎', currentValue: 50, targetValue: 50, category: 'daily' },
  { id: '4', title: 'Seri Giriş', desc: 'Uygulamaya üst üste 3 gün gir', rewardValue: 'VIP', rewardIcon: '👑', currentValue: 2, targetValue: 3, category: 'daily' },
  
  { id: '5', title: 'Elmas Kralı', desc: 'Hafta boyunca toplam 500 Elmas hediye et', rewardValue: 'Ateşli Çerçeve', rewardIcon: '🔥', currentValue: 120, targetValue: 500, category: 'weekly' },
  { id: '6', title: 'Sadık İzleyici', desc: 'Hafta boyunca 3 gün aktif ol', rewardValue: '50', rewardIcon: '💎', currentValue: 3, targetValue: 3, category: 'weekly' },
  
  { id: '7', title: 'Profili Tamamla', desc: 'Profil fotoğrafı ve biyografini ekle', rewardValue: '20', rewardIcon: '💎', currentValue: 1, targetValue: 1, category: 'starter' },
  { id: '8', title: 'İlk Takip', desc: '3 yayıncıyı takip et', rewardValue: 'VIP Avatar', rewardIcon: '🌟', currentValue: 0, targetValue: 3, category: 'starter' },
];

const TasksPage: React.FC<TasksPageProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<'daily' | 'weekly' | 'starter'>('daily');
  const [tasks] = useState<Task[]>(INITIAL_TASKS);
  const [claimedIds, setClaimedIds] = useState<Set<string>>(new Set());

  const handleClaim = (taskId: string) => {
    setClaimedIds(prev => {
      const next = new Set(prev);
      next.add(taskId);
      return next;
    });
    // Basit bir kutlama alerti, daha sonra animasyona çevrilebilir.
    alert("Tebrikler! Ödülünüz bakiyenize/hesabınıza eklendi! 🎉");
  };

  const filteredTasks = tasks.filter(t => t.category === activeTab);

  return (
    <div style={{
      width: '100%', height: '100%',
      background: 'linear-gradient(160deg, #12121c, #0e0e18)',
      display: 'flex', flexDirection: 'column',
      position: 'relative', overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '50px 20px 20px',
        background: 'rgba(255,255,255,.02)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 15,
        position: 'relative', zIndex: 10
      }}>
        <button onClick={onBack} className="icon-btn" style={{ background: 'rgba(255,255,255,.05)' }}>
          ←
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 24 }}>🎯</span>
          <h2 className="syne" style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Görevler</h2>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', padding: '15px 20px', gap: 10, overflowX: 'auto', flexShrink: 0, WebkitOverflowScrolling: 'touch' }}>
        {[
          { id: 'daily', label: 'Günlük', icon: '⏱️', color: 'var(--cyan)' },
          { id: 'weekly', label: 'Haftalık', icon: '📅', color: 'var(--purple)' },
          { id: 'starter', label: 'Başlangıç', icon: '🚀', color: 'var(--coral)' }
        ].map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '10px 16px', borderRadius: 20, whiteSpace: 'nowrap',
                background: isActive ? `${tab.color}22` : 'rgba(255,255,255,.03)',
                border: isActive ? `1px solid ${tab.color}55` : '1px solid transparent',
                color: isActive ? tab.color : 'var(--muted)',
                fontWeight: 600, fontFamily: 'Syne', transition: 'all 0.2s', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6
              }}>
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 90px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filteredTasks.map((task) => {
          const isComplete = task.currentValue >= task.targetValue;
          const isClaimed = claimedIds.has(task.id);
          const progressPercent = Math.min(100, Math.round((task.currentValue / task.targetValue) * 100));

          return (
            <div key={task.id} style={{
              background: 'rgba(255,255,255,.02)',
              border: '1px solid rgba(255,255,255,.05)',
              borderRadius: 20, padding: '18px',
              position: 'relative', overflow: 'hidden',
              opacity: isClaimed ? 0.6 : 1, transition: 'all 0.3s',
              display: 'flex', flexDirection: 'column', gap: 16,
              boxShadow: isComplete && !isClaimed ? '0 0 20px rgba(0,229,204,0.08)' : 'none'
            }}>
              {/* Top Row: Title & Reward */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, paddingRight: 10 }}>
                  <div className="syne" style={{ fontSize: 17, fontWeight: 800, marginBottom: 5, color: '#fff', letterSpacing: '-0.02em' }}>
                    {task.title}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.4 }}>
                    {task.desc}
                  </div>
                </div>
                {/* Reward Badge */}
                <div style={{
                  background: 'rgba(255,255,255,.06)', borderRadius: 12,
                  padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6,
                  border: '1px solid rgba(255,255,255,.08)', whiteSpace: 'nowrap', flexShrink: 0
                }}>
                  <span style={{ fontSize: 14 }}>{task.rewardIcon}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, fontFamily: 'Syne', color: '#fff' }}>{task.rewardValue}</span>
                </div>
              </div>

              {/* Middle Row: Progress */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>
                  <span>İlerleme durumu</span>
                  <span className="syne" style={{ color: isComplete ? 'var(--green)' : 'var(--muted)', fontWeight: 700 }}>{task.currentValue} / {task.targetValue}</span>
                </div>
                <div style={{ width: '100%', height: 8, background: 'rgba(0,0,0,0.4)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    width: `${progressPercent}%`, height: '100%',
                    background: isComplete ? 'var(--green)' : 'linear-gradient(90deg, var(--cyan), var(--purple))',
                    borderRadius: 4, transition: 'width 0.4s ease'
                  }} />
                </div>
              </div>

              {/* Bottom Row: Action Button */}
              <button 
                onClick={() => handleClaim(task.id)}
                disabled={!isComplete || isClaimed}
                style={{
                  width: '100%', padding: '12px', borderRadius: 14,
                  border: 'none', fontFamily: 'Syne', fontWeight: 800, fontSize: 14,
                  background: isClaimed ? 'rgba(255,255,255,0.05)' : (isComplete ? 'linear-gradient(135deg, var(--cyan), var(--purple))' : 'rgba(255,255,255,0.03)'),
                  color: isClaimed ? 'var(--muted)' : (isComplete ? '#fff' : 'rgba(255,255,255,0.3)'),
                  cursor: (isComplete && !isClaimed) ? 'pointer' : 'not-allowed',
                  boxShadow: (isComplete && !isClaimed) ? '0 4px 15px rgba(0,229,204,0.3)' : 'none',
                  transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  minHeight: '44px'
                }}>
                {isClaimed ? '✓ Ödül Alındı' : (isComplete ? 'Ödülü Al 🎁' : 'Göreve Devam Et')}
              </button>
            </div>
          );
        })}

        {filteredTasks.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🎉</div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Bu kategorideki tüm görevleri tamamladın!</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TasksPage;
